import React, { useState, useEffect, useCallback, useRef } from 'react';
import { Helmet } from 'react-helmet';
import { CheckCircle, Clock, DollarSign, Download, RefreshCw, Banknote, Receipt, AlertCircle, ChevronDown, ChevronUp, Send, Shield, Zap, FileText } from 'lucide-react';
import { supabase } from '../../lib/supabase';
import { useAuth } from '../../contexts/AuthContext';

// ─── Helpers ──────────────────────────────────────────────────────────────────
const fmt = (amount, currency = 'XOF') => {
  if (!amount && amount !== 0) return '—';
  return new Intl.NumberFormat('fr-FR', { style: 'currency', currency, maximumFractionDigits: 0 })?.format(amount);
};

const fmtDate = (d) => {
  if (!d) return '—';
  return new Intl.DateTimeFormat('fr-FR', { day: '2-digit', month: 'long', year: 'numeric' })?.format(new Date(d));
};

const fmtDateTime = (d) => {
  if (!d) return '—';
  return new Intl.DateTimeFormat('fr-FR', { day: '2-digit', month: 'short', year: 'numeric', hour: '2-digit', minute: '2-digit' })?.format(new Date(d));
};

// ─── Settlement Status Config ─────────────────────────────────────────────────
const SETTLEMENT_STEPS = [
  { key: 'escrow_released', label: 'Fonds libérés du séquestre', icon: Zap },
  { key: 'processing', label: 'Traitement du règlement', icon: RefreshCw },
  { key: 'bank_transfer_initiated', label: 'Virement bancaire initié', icon: Send },
  { key: 'bank_arrival', label: 'Arrivée sur votre compte', icon: Banknote },
];

const PAYOUT_STATUS = {
  pending:    { label: 'En attente',     bg: 'bg-amber-50',   text: 'text-amber-700',   border: 'border-amber-200',  icon: Clock },
  approved:   { label: 'Approuvé',       bg: 'bg-blue-50',    text: 'text-blue-700',    border: 'border-blue-200',   icon: CheckCircle },
  processing: { label: 'En traitement',  bg: 'bg-purple-50',  text: 'text-purple-700',  border: 'border-purple-200', icon: RefreshCw },
  paid:       { label: 'Versé',          bg: 'bg-green-50',   text: 'text-green-700',   border: 'border-green-200',  icon: CheckCircle },
  failed:     { label: 'Échoué',         bg: 'bg-red-50',     text: 'text-red-700',     border: 'border-red-200',    icon: AlertCircle },
  cancelled:  { label: 'Annulé',         bg: 'bg-gray-100',   text: 'text-gray-600',    border: 'border-gray-200',   icon: AlertCircle },
};

const StatusBadge = ({ status }) => {
  const cfg = PAYOUT_STATUS?.[status] || PAYOUT_STATUS?.pending;
  const IconComp = cfg?.icon;
  return (
    <span className={`inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-xs font-semibold border ${cfg?.bg} ${cfg?.text} ${cfg?.border}`}>
      <IconComp className="w-3 h-3" />
      {cfg?.label}
    </span>
  );
};

// ─── Settlement Timeline ──────────────────────────────────────────────────────
const SettlementTimeline = ({ payout }) => {
  const getStepStatus = (stepKey) => {
    const status = payout?.status;
    if (stepKey === 'escrow_released') return status !== 'pending' ? 'done' : 'active';
    if (stepKey === 'processing') {
      if (['paid']?.includes(status)) return 'done';
      if (['approved', 'processing']?.includes(status)) return 'active';
      return 'pending';
    }
    if (stepKey === 'bank_transfer_initiated') {
      if (status === 'paid') return 'done';
      if (status === 'processing') return 'active';
      return 'pending';
    }
    if (stepKey === 'bank_arrival') {
      return status === 'paid' ? 'done' : 'pending';
    }
    return 'pending';
  };

  const bankArrivalDate = payout?.paid_at
    ? new Date(new Date(payout.paid_at).getTime() + 2 * 24 * 60 * 60 * 1000)
    : null;

  return (
    <div className="space-y-0">
      {SETTLEMENT_STEPS?.map((step, idx) => {
        const stepStatus = getStepStatus(step?.key);
        const IconComp = step?.icon;
        const isLast = idx === SETTLEMENT_STEPS?.length - 1;

        return (
          <div key={step?.key} className="flex gap-4">
            <div className="flex flex-col items-center">
              <div className={`w-9 h-9 rounded-full flex items-center justify-center flex-shrink-0 transition-all ${
                stepStatus === 'done'
                  ? 'bg-green-500 text-white shadow-sm shadow-green-200'
                  : stepStatus === 'active' ?'bg-blue-500 text-white shadow-sm shadow-blue-200 animate-pulse' :'bg-gray-100 text-gray-400'
              }`}>
                {stepStatus === 'done' ? (
                  <CheckCircle className="w-4 h-4" />
                ) : (
                  <IconComp className={`w-4 h-4 ${stepStatus === 'active' ? '' : ''}`} />
                )}
              </div>
              {!isLast && (
                <div className={`w-0.5 h-8 mt-1 ${stepStatus === 'done' ? 'bg-green-300' : 'bg-gray-200'}`} />
              )}
            </div>
            <div className="pb-6 flex-1 min-w-0">
              <p className={`text-sm font-semibold ${
                stepStatus === 'done' ? 'text-green-700' :
                stepStatus === 'active' ? 'text-blue-700' : 'text-gray-400'
              }`}>
                {step?.label}
              </p>
              {step?.key === 'bank_arrival' && bankArrivalDate && (
                <p className="text-xs text-gray-500 mt-0.5">
                  Estimé: {fmtDate(bankArrivalDate?.toISOString())}
                </p>
              )}
              {step?.key === 'processing' && payout?.approved_at && (
                <p className="text-xs text-gray-500 mt-0.5">{fmtDateTime(payout?.approved_at)}</p>
              )}
              {step?.key === 'bank_transfer_initiated' && payout?.paid_at && (
                <p className="text-xs text-gray-500 mt-0.5">{fmtDateTime(payout?.paid_at)}</p>
              )}
            </div>
          </div>
        );
      })}
    </div>
  );
};

// ─── Receipt Modal ────────────────────────────────────────────────────────────
const ReceiptModal = ({ payout, onClose }) => {
  const receiptRef = React.useRef(null);
  const receiptNumber = `KS-${payout?.id?.slice(0, 8)?.toUpperCase()}-${new Date(payout?.created_at || Date.now())?.getFullYear()}`;

  const handlePrint = () => {
    const content = receiptRef?.current?.innerHTML;
    if (!content) return;
    const win = window.open('', '_blank');
    win?.document?.write(`
      <html>
        <head>
          <title>Reçu de règlement - ${receiptNumber}</title>
          <style>
            body { font-family: -apple-system, sans-serif; max-width: 600px; margin: 40px auto; color: #111; }
            .header { background: #0f0a00; color: white; padding: 24px; border-radius: 12px; margin-bottom: 24px; }
            .row { display: flex; justify-content: space-between; padding: 8px 0; border-bottom: 1px solid #f0f0f0; font-size: 14px; }
            .label { color: #666; }
            .value { font-weight: 600; }
            .total { font-size: 18px; font-weight: 700; color: #16a34a; }
            .badge { display: inline-block; background: #dcfce7; color: #166534; padding: 4px 12px; border-radius: 100px; font-size: 12px; font-weight: 700; }
          </style>
        </head>
        <body>${content}</body>
      </html>
    `);
    win?.document?.close();
    win?.print();
  };

  const grossAmount = payout?.amount || 0;
  const taxWithheld = payout?.tax_withholding_amount || 0;
  const fxFee = payout?.fx_fee_amount || 0;
  const netAmount = payout?.net_after_tax || payout?.net_payout_amount || (grossAmount - taxWithheld - fxFee);

  return (
    <div className="fixed inset-0 bg-black/60 z-50 flex items-center justify-center p-4">
      <div className="bg-white rounded-2xl shadow-2xl w-full max-w-lg max-h-[90vh] overflow-y-auto">
        <div className="p-5 border-b border-gray-100 flex items-center justify-between">
          <h3 className="text-lg font-bold text-gray-900">Reçu de règlement</h3>
          <button onClick={onClose} className="text-gray-400 hover:text-gray-600 p-1">
            <ChevronDown className="w-5 h-5" />
          </button>
        </div>

        <div ref={receiptRef} className="p-6 space-y-5">
          {/* Header */}
          <div className="bg-gray-900 text-white rounded-xl p-5">
            <div className="flex items-center gap-3 mb-4">
              <div className="w-9 h-9 bg-yellow-500 rounded-lg flex items-center justify-center">
                <span className="text-black font-bold text-sm">KS</span>
              </div>
              <div>
                <p className="font-bold text-white">Kapital States</p>
                <p className="text-xs text-gray-400">Abidjan, Côte d'Ivoire</p>
              </div>
            </div>
            <div className="flex items-center justify-between">
              <div>
                <p className="text-xs text-gray-400 uppercase tracking-wider">Reçu de règlement</p>
                <p className="text-lg font-bold text-yellow-400 mt-0.5">{receiptNumber}</p>
              </div>
              <span className="bg-green-500/20 text-green-400 text-xs font-bold px-3 py-1 rounded-full border border-green-500/30">
                {PAYOUT_STATUS?.[payout?.status]?.label || payout?.status}
              </span>
            </div>
          </div>

          {/* Transaction details */}
          <div className="space-y-2">
            <p className="text-xs font-bold text-gray-500 uppercase tracking-wider">Détails de la transaction</p>
            {[
              { label: 'Référence transaction', value: payout?.transaction_ref || '—' },
              { label: 'Propriété', value: payout?.property_title || '—' },
              { label: 'Date de création', value: fmtDate(payout?.created_at) },
              { label: 'Canal de paiement', value: payout?.payout_channel === 'bank_transfer' ? 'Virement bancaire' : payout?.payout_channel === 'mobile_money' ? 'Mobile Money' : payout?.payout_channel || '—' },
              { label: 'Devise', value: payout?.payout_currency || 'XOF' },
            ]?.map(row => (
              <div key={row?.label} className="flex justify-between py-2 border-b border-gray-100 text-sm">
                <span className="text-gray-500">{row?.label}</span>
                <span className="font-semibold text-gray-900">{row?.value}</span>
              </div>
            ))}
          </div>

          {/* Financial breakdown */}
          <div className="bg-gray-50 rounded-xl p-4 space-y-2">
            <p className="text-xs font-bold text-gray-500 uppercase tracking-wider mb-3">Détail financier</p>
            {[
              { label: 'Montant brut séquestre', value: fmt(grossAmount, payout?.payout_currency || 'XOF'), highlight: false },
              { label: 'Retenue fiscale', value: taxWithheld > 0 ? `-${fmt(taxWithheld, payout?.payout_currency || 'XOF')}` : '—', highlight: false },
              { label: 'Frais de conversion FX', value: fxFee > 0 ? `-${fmt(fxFee, payout?.payout_currency || 'XOF')}` : '—', highlight: false },
              ...(payout?.fx_rate_applied ? [{ label: `Taux de change appliqué`, value: `1 XOF = ${payout?.fx_rate_applied} ${payout?.payout_currency}`, highlight: false }] : []),
            ]?.map(row => (
              <div key={row?.label} className="flex justify-between text-sm py-1">
                <span className="text-gray-500">{row?.label}</span>
                <span className={`font-semibold ${row?.highlight ? 'text-green-700 text-base' : 'text-gray-700'}`}>{row?.value}</span>
              </div>
            ))}
            <div className="flex justify-between pt-3 border-t border-gray-200">
              <span className="font-bold text-gray-900">Montant net versé</span>
              <span className="font-bold text-green-700 text-lg">{fmt(netAmount, payout?.payout_currency || 'XOF')}</span>
            </div>
          </div>

          {/* Settlement dates */}
          {(payout?.approved_at || payout?.paid_at) && (
            <div className="space-y-2">
              <p className="text-xs font-bold text-gray-500 uppercase tracking-wider">Dates de règlement</p>
              {payout?.approved_at && (
                <div className="flex justify-between text-sm py-1 border-b border-gray-100">
                  <span className="text-gray-500">Date d'approbation</span>
                  <span className="font-semibold text-gray-900">{fmtDate(payout?.approved_at)}</span>
                </div>
              )}
              {payout?.paid_at && (
                <div className="flex justify-between text-sm py-1 border-b border-gray-100">
                  <span className="text-gray-500">Date de virement</span>
                  <span className="font-semibold text-gray-900">{fmtDate(payout?.paid_at)}</span>
                </div>
              )}
              {payout?.paid_at && (
                <div className="flex justify-between text-sm py-1">
                  <span className="text-gray-500">Arrivée estimée en banque</span>
                  <span className="font-semibold text-blue-700">
                    {fmtDate(new Date(new Date(payout.paid_at).getTime() + 2 * 24 * 60 * 60 * 1000)?.toISOString())}
                  </span>
                </div>
              )}
            </div>
          )}

          <p className="text-xs text-gray-400 text-center pt-2">
            Ce reçu est généré automatiquement par Kapital States · kapitalstates.com
          </p>
        </div>

        <div className="p-5 border-t border-gray-100 flex gap-3">
          <button
            onClick={onClose}
            className="flex-1 px-4 py-2.5 border border-gray-300 text-gray-700 rounded-xl text-sm font-semibold hover:bg-gray-50 transition-colors"
          >
            Fermer
          </button>
          <button
            onClick={handlePrint}
            className="flex-1 px-4 py-2.5 bg-gray-900 text-white rounded-xl text-sm font-semibold hover:bg-gray-800 transition-colors flex items-center justify-center gap-2"
          >
            <Download className="w-4 h-4" />
            Imprimer / Sauvegarder
          </button>
        </div>
      </div>
    </div>
  );
};

// ─── Payout Card ──────────────────────────────────────────────────────────────
const PayoutCard = ({ payout }) => {
  const [expanded, setExpanded] = useState(false);
  const [showReceipt, setShowReceipt] = useState(false);

  const netAmount = payout?.net_after_tax || payout?.net_payout_amount || payout?.amount;

  return (
    <>
      <div className="bg-white rounded-2xl border border-gray-200 shadow-sm hover:shadow-md transition-all">
        <div className="p-5">
          <div className="flex items-start justify-between gap-3">
            <div className="flex-1 min-w-0">
              <div className="flex items-center gap-2 flex-wrap mb-2">
                <StatusBadge status={payout?.status} />
                {payout?.payout_channel && (
                  <span className="text-xs bg-gray-100 text-gray-600 px-2 py-0.5 rounded font-medium">
                    {payout?.payout_channel === 'bank_transfer' ? '🏦 Virement' :
                     payout?.payout_channel === 'mobile_money' ? '📱 Mobile Money' : payout?.payout_channel}
                  </span>
                )}
              </div>
              <p className="text-sm font-bold text-gray-900 truncate">
                {payout?.property_title || payout?.transaction_ref || `Paiement #${payout?.id?.slice(0, 8)}`}
              </p>
              <p className="text-xs text-gray-500 mt-0.5">{payout?.transaction_ref || '—'}</p>
            </div>
            <div className="text-right flex-shrink-0">
              <p className="text-xl font-bold text-green-700">{fmt(netAmount, payout?.payout_currency || 'XOF')}</p>
              <p className="text-xs text-gray-400 mt-0.5">Net versé</p>
            </div>
          </div>

          {/* Quick timeline indicator */}
          <div className="mt-4 flex items-center gap-1">
            {SETTLEMENT_STEPS?.map((step, idx) => {
              const status = payout?.status;
              let done = false;
              if (step?.key === 'escrow_released') done = status !== 'pending';
              if (step?.key === 'processing') done = ['paid']?.includes(status);
              if (step?.key === 'bank_transfer_initiated') done = status === 'paid';
              if (step?.key === 'bank_arrival') done = status === 'paid';
              const isLast = idx === SETTLEMENT_STEPS?.length - 1;
              return (
                <React.Fragment key={step?.key}>
                  <div className={`w-5 h-5 rounded-full flex items-center justify-center flex-shrink-0 ${
                    done ? 'bg-green-500' : 'bg-gray-200'
                  }`}>
                    {done && <CheckCircle className="w-3 h-3 text-white" />}
                  </div>
                  {!isLast && <div className={`flex-1 h-0.5 ${done ? 'bg-green-300' : 'bg-gray-200'}`} />}
                </React.Fragment>
              );
            })}
          </div>
          <div className="flex justify-between mt-1">
            {SETTLEMENT_STEPS?.map(s => (
              <span key={s?.key} className="text-[9px] text-gray-400 text-center" style={{ width: '20%' }}>
                {s?.label?.split(' ')?.[0]}
              </span>
            ))}
          </div>

          <div className="flex items-center gap-2 mt-4">
            <button
              onClick={() => setShowReceipt(true)}
              className="flex items-center gap-1.5 px-3 py-1.5 bg-gray-900 text-white rounded-lg text-xs font-semibold hover:bg-gray-800 transition-colors"
            >
              <Receipt className="w-3 h-3" />
              Voir le reçu
            </button>
            <button
              onClick={() => setExpanded(e => !e)}
              className="flex items-center gap-1.5 px-3 py-1.5 border border-gray-200 text-gray-600 rounded-lg text-xs font-medium hover:bg-gray-50 transition-colors"
            >
              {expanded ? <ChevronUp className="w-3 h-3" /> : <ChevronDown className="w-3 h-3" />}
              {expanded ? 'Moins' : 'Détails'}
            </button>
          </div>
        </div>

        {expanded && (
          <div className="px-5 pb-5 border-t border-gray-100 pt-4 grid grid-cols-1 md:grid-cols-2 gap-5">
            {/* Financial details */}
            <div>
              <p className="text-xs font-bold text-gray-500 uppercase tracking-wider mb-3">Détail financier</p>
              <div className="space-y-2 text-sm">
                {[
                  { label: 'Montant brut', value: fmt(payout?.amount, payout?.payout_currency || 'XOF') },
                  { label: 'Retenue fiscale', value: payout?.tax_withholding_amount ? `-${fmt(payout?.tax_withholding_amount, payout?.payout_currency || 'XOF')}` : '—' },
                  { label: 'Frais FX', value: payout?.fx_fee_amount ? `-${fmt(payout?.fx_fee_amount, payout?.payout_currency || 'XOF')}` : '—' },
                  { label: 'Taux fiscal', value: payout?.tax_rate_applied ? `${(payout?.tax_rate_applied * 100)?.toFixed(1)}%` : '—' },
                  { label: 'Taux de change', value: payout?.fx_rate_applied ? `${payout?.fx_rate_applied}` : '—' },
                ]?.map(row => (
                  <div key={row?.label} className="flex justify-between">
                    <span className="text-gray-500">{row?.label}</span>
                    <span className="font-semibold text-gray-800">{row?.value}</span>
                  </div>
                ))}
                <div className="flex justify-between pt-2 border-t border-gray-100">
                  <span className="font-bold text-gray-900">Net versé</span>
                  <span className="font-bold text-green-700">{fmt(netAmount, payout?.payout_currency || 'XOF')}</span>
                </div>
              </div>
            </div>

            {/* Settlement timeline */}
            <div>
              <p className="text-xs font-bold text-gray-500 uppercase tracking-wider mb-3">Timeline de règlement</p>
              <SettlementTimeline payout={payout} />
            </div>
          </div>
        )}
      </div>

      {showReceipt && (
        <ReceiptModal payout={payout} onClose={() => setShowReceipt(false)} />
      )}
    </>
  );
};

// ─── Main Page ────────────────────────────────────────────────────────────────
const EscrowSettlementDashboard = () => {
  const { user } = useAuth();
  const [payouts, setPayouts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [filterStatus, setFilterStatus] = useState('all');

  const fetchPayouts = useCallback(async () => {
    if (!user?.id) return;
    try {
      setRefreshing(true);
      const { data, error } = await supabase?.from('commission_payouts')?.select('*')?.eq('recipient_id', user?.id)?.order('created_at', { ascending: false });
      if (error) throw error;
      setPayouts(data || []);
    } catch (err) {
      console.error('[EscrowSettlementDashboard] fetch error:', err);
    } finally {
      setLoading(false);
      setRefreshing(false);
    }
  }, [user?.id]);

  useEffect(() => { fetchPayouts(); }, [fetchPayouts]);

  const filtered = payouts?.filter(p => filterStatus === 'all' || p?.status === filterStatus);

  const totalPaid = payouts?.filter(p => p?.status === 'paid')?.reduce((s, p) => s + (p?.net_after_tax || p?.net_payout_amount || p?.amount || 0), 0);
  const totalPending = payouts?.filter(p => ['pending', 'approved', 'processing']?.includes(p?.status))?.reduce((s, p) => s + (p?.amount || 0), 0);
  const paidCount = payouts?.filter(p => p?.status === 'paid')?.length;
  const pendingCount = payouts?.filter(p => ['pending', 'approved', 'processing']?.includes(p?.status))?.length;

  return (
    <>
      <Helmet>
        <title>Règlements & Paiements — Kapital States</title>
        <meta name="description" content="Suivez vos fonds libérés du séquestre, le statut de traitement et vos confirmations de paiement." />
      </Helmet>

      <div className="min-h-screen bg-gray-50">
        {/* Header */}
        <div className="bg-white border-b border-gray-200 px-6 py-5">
          <div className="max-w-4xl mx-auto flex items-center justify-between">
            <div>
              <h1 className="text-2xl font-bold text-gray-900">Règlements & Paiements</h1>
              <p className="text-sm text-gray-500 mt-0.5">Fonds libérés du séquestre et confirmations de virement</p>
            </div>
            <button
              onClick={fetchPayouts}
              disabled={refreshing}
              className="flex items-center gap-2 px-4 py-2 border border-gray-300 text-gray-600 rounded-xl text-sm font-medium hover:bg-gray-50 transition-colors disabled:opacity-50"
            >
              <RefreshCw className={`w-4 h-4 ${refreshing ? 'animate-spin' : ''}`} />
              Actualiser
            </button>
          </div>
        </div>

        <div className="max-w-4xl mx-auto px-6 py-8 space-y-6">
          {/* Summary cards */}
          <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
            {[
              { label: 'Total versé', value: fmt(totalPaid), icon: CheckCircle, color: 'bg-green-100 text-green-600', sub: `${paidCount} paiement${paidCount !== 1 ? 's' : ''}` },
              { label: 'En attente', value: fmt(totalPending), icon: Clock, color: 'bg-amber-100 text-amber-600', sub: `${pendingCount} en cours` },
              { label: 'Transactions', value: payouts?.length, icon: FileText, color: 'bg-blue-100 text-blue-600', sub: 'Total' },
              { label: 'Devise principale', value: 'XOF', icon: DollarSign, color: 'bg-purple-100 text-purple-600', sub: 'Franc CFA' },
            ]?.map(s => (
              <div key={s?.label} className="bg-white rounded-xl border border-gray-200 p-4 shadow-sm">
                <div className={`w-9 h-9 rounded-lg flex items-center justify-center ${s?.color} mb-3`}>
                  <s.icon className="w-4 h-4" />
                </div>
                <p className="text-xl font-bold text-gray-900">{s?.value}</p>
                <p className="text-xs font-semibold text-gray-700 mt-0.5">{s?.label}</p>
                <p className="text-xs text-gray-400">{s?.sub}</p>
              </div>
            ))}
          </div>

          {/* Info banner */}
          <div className="bg-blue-50 border border-blue-200 rounded-xl p-4 flex items-start gap-3">
            <Shield className="w-5 h-5 text-blue-600 flex-shrink-0 mt-0.5" />
            <div>
              <p className="text-sm font-semibold text-blue-800">Délai de virement bancaire</p>
              <p className="text-sm text-blue-700 mt-0.5">
                Les virements bancaires arrivent généralement sous <strong>1 à 3 jours ouvrés</strong> après l'initiation.
                Les paiements Mobile Money sont traités sous <strong>24h</strong>.
              </p>
            </div>
          </div>

          {/* Filter */}
          <div className="flex items-center gap-3">
            <span className="text-sm font-medium text-gray-600">Filtrer:</span>
            {['all', 'pending', 'approved', 'processing', 'paid', 'failed']?.map(s => (
              <button
                key={s}
                onClick={() => setFilterStatus(s)}
                className={`px-3 py-1.5 rounded-lg text-xs font-semibold transition-colors ${
                  filterStatus === s
                    ? 'bg-gray-900 text-white' :'bg-white border border-gray-200 text-gray-600 hover:border-gray-300'
                }`}
              >
                {s === 'all' ? 'Tous' : PAYOUT_STATUS?.[s]?.label || s}
              </button>
            ))}
          </div>

          {/* Payouts list */}
          {loading ? (
            <div className="space-y-4">
              {[...Array(3)]?.map((_, i) => (
                <div key={i} className="bg-white rounded-2xl border border-gray-200 p-5 animate-pulse">
                  <div className="flex justify-between mb-4">
                    <div className="space-y-2">
                      <div className="h-5 w-24 bg-gray-200 rounded-full" />
                      <div className="h-4 w-40 bg-gray-200 rounded" />
                    </div>
                    <div className="h-7 w-28 bg-gray-200 rounded" />
                  </div>
                  <div className="h-2 w-full bg-gray-100 rounded-full" />
                </div>
              ))}
            </div>
          ) : filtered?.length === 0 ? (
            <div className="bg-white rounded-2xl border border-gray-200 p-16 text-center">
              <Banknote className="w-12 h-12 text-gray-300 mx-auto mb-4" />
              <p className="text-gray-500 font-semibold">Aucun paiement trouvé</p>
              <p className="text-sm text-gray-400 mt-1">
                {filterStatus === 'all' ?'Vos paiements de séquestre apparaîtront ici une fois libérés.' :'Aucun paiement avec ce statut.'}
              </p>
            </div>
          ) : (
            <div className="space-y-4">
              {filtered?.map(p => (
                <PayoutCard key={p?.id} payout={p} />
              ))}
            </div>
          )}
        </div>
      </div>
    </>
  );
};

export default EscrowSettlementDashboard;
